"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

/**
 * Grafiche animate per la pagina Progetti (brand Dimensione 4, motif ciano).
 * Solo transform/opacity, sempre con useReducedMotion (ridotto -> contenuto
 * visibile subito, niente loop). Loop gated da useInView (fermo fuori schermo).
 * Decorative: aria-hidden.
 */

const S = "hsl(var(--primary))";

/**
 * Companion dell'InBreve: un "portfolio che prende forma".
 * Una griglia di card (slot) si costruisce in cascata: il telaio compare,
 * poi una card piena (ciano) scorre verso l'alto come progetto "pubblicato",
 * mentre una barra di avanzamento la accompagna. In loop morbido.
 *
 * SOLO transform/opacity: le card entrano in opacity + translate y; la card
 * "in evidenza" trasla in y e cambia opacity; la barra di progress scala in x
 * (scaleX con origine a sinistra). Reduced-motion -> tutto fermo e visibile,
 * con la card di punta gia' in posizione. In-view gate per fermare il loop.
 */
export function PortfolioBuildingVisual() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const loop = inView && !reduce;

  // Slot della griglia (telaio tratteggiato sempre presente).
  const slots = [
    { x: 8, y: 14, w: 40, h: 26 },
    { x: 52, y: 14, w: 40, h: 26 },
    { x: 8, y: 46, w: 40, h: 26 },
    { x: 52, y: 46, w: 40, h: 26 },
  ];

  // Card "piene" che compaiono in cascata (progetti che arrivano).
  const cards = [
    { x: 8, y: 14, w: 40, h: 26, delay: 0 },
    { x: 52, y: 46, w: 40, h: 26, delay: 0.45 },
  ];

  return (
    <div
      ref={ref}
      className="pointer-events-none relative mx-auto w-full max-w-[19rem] select-none"
      aria-hidden
    >
      <div className="absolute inset-0 rounded-3xl bg-primary/10 blur-2xl" />
      <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface/50 p-4">
        <div className="absolute inset-0 pattern-dots opacity-30" />
        <svg viewBox="0 0 100 86" className="relative h-auto w-full">
          {/* Telaio: slot vuoti (tratteggiati) sempre presenti */}
          <g stroke={S} fill="none" strokeWidth={1.1} opacity={0.32}>
            {slots.map((s, i) => (
              <rect
                key={i}
                x={s.x}
                y={s.y}
                width={s.w}
                height={s.h}
                rx={3}
                strokeDasharray="3 3"
              />
            ))}
          </g>

          {/* Card piene che arrivano in cascata: translate y + opacity */}
          {cards.map((c, i) => (
            <motion.g
              key={i}
              initial={false}
              animate={
                loop
                  ? { y: [6, 0, 0, 0, 6], opacity: [0, 1, 1, 1, 0] }
                  : { y: 0, opacity: 1 }
              }
              transition={
                loop
                  ? {
                      duration: 4,
                      times: [0, 0.18, 0.5, 0.82, 1],
                      delay: c.delay,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }
                  : { duration: 0 }
              }
            >
              <rect
                x={c.x}
                y={c.y}
                width={c.w}
                height={c.h}
                rx={3}
                fill={S}
                fillOpacity={0.14}
                stroke={S}
                strokeWidth={1.4}
              />
              {/* "thumbnail" + righe testo dentro la card */}
              <rect x={c.x + 4} y={c.y + 4} width={c.w - 8} height={9} rx={1.5} fill={S} fillOpacity={0.45} />
              <rect x={c.x + 4} y={c.y + 16} width={c.w - 14} height={2.4} rx={1.2} fill={S} fillOpacity={0.6} />
              <rect x={c.x + 4} y={c.y + 21} width={c.w - 20} height={2.4} rx={1.2} fill={S} fillOpacity={0.35} />
            </motion.g>
          ))}

          {/* Barra di avanzamento "in costruzione": scaleX da sinistra */}
          <rect x={8} y={80} width={84} height={2.4} rx={1.2} fill={S} opacity={0.14} />
          <motion.rect
            x={8}
            y={80}
            width={84}
            height={2.4}
            rx={1.2}
            fill={S}
            style={{ transformBox: "fill-box", transformOrigin: "left center" }}
            initial={false}
            animate={loop ? { scaleX: [0, 1, 1, 0] } : { scaleX: 0.62 }}
            transition={
              loop
                ? { duration: 4, times: [0, 0.5, 0.85, 1], repeat: Infinity, ease: "easeInOut" }
                : { duration: 0 }
            }
          />
        </svg>
      </div>
    </div>
  );
}

/**
 * Grafica per la card "case study in arrivo": tre mini-card che entrano in
 * cascata da sinistra (progetti che si aggiungono al feed), con un puntino di
 * stato che pulsa. Compatta, orizzontale, per restare a sinistra della card.
 *
 * SOLO transform/opacity: le card entrano in opacity + translate x in loop
 * morbido; il puntino di stato pulsa in scale. Reduced-motion -> visibile e
 * fermo. In-view gate.
 */
export function CaseStudyTeaserVisual() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const loop = inView && !reduce;

  const rows = [0, 1, 2];

  return (
    <div
      ref={ref}
      className="pointer-events-none w-full max-w-sm select-none"
      aria-hidden
    >
      <svg viewBox="0 0 120 78" className="h-auto w-full">
        {/* binario verticale ciano (timeline dei case study) */}
        <line x1={9} y1={10} x2={9} y2={68} stroke={S} strokeWidth={1.4} opacity={0.3} strokeLinecap="round" />

        {rows.map((r) => {
          const y = 8 + r * 24;
          return (
            <motion.g
              key={r}
              initial={false}
              animate={
                loop
                  ? { x: [10, 0, 0, 10], opacity: [0, 1, 1, 0] }
                  : { x: 0, opacity: r === 0 ? 1 : 0.5 }
              }
              transition={
                loop
                  ? {
                      duration: 3.6,
                      times: [0, 0.22, 0.78, 1],
                      delay: r * 0.4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }
                  : { duration: 0 }
              }
            >
              {/* nodo sul binario */}
              <circle cx={9} cy={y + 8} r={2.6} fill={S} />
              {/* card riga */}
              <rect
                x={20}
                y={y}
                width={94}
                height={16}
                rx={3}
                fill={S}
                fillOpacity={0.1}
                stroke={S}
                strokeWidth={1.1}
              />
              <rect x={25} y={y + 4} width={30} height={2.6} rx={1.3} fill={S} fillOpacity={0.7} />
              <rect x={25} y={y + 9.5} width={56} height={2.4} rx={1.2} fill={S} fillOpacity={0.3} />
              {/* puntino di stato che pulsa */}
              <motion.circle
                cx={106}
                cy={y + 8}
                r={2.4}
                fill={S}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                animate={loop ? { scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] } : undefined}
                transition={
                  loop
                    ? { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: r * 0.3 }
                    : undefined
                }
              />
            </motion.g>
          );
        })}
      </svg>
    </div>
  );
}
